import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { TreeMapMetric } from '../../../../../store/strategy-store/strategy-store.type';
import { useFilteredInitiatives } from '../../../strategy.hooks';
import type { TreeMapData } from '../main-section.type';
import { calculateTreeMapData } from './tree-map.helpers';

/**
 * Custom hook that provides data for the tree map visualization
 *
 * This hook calculates and returns tree map data based on the selected metric (cost or effort)
 * and filtered initiatives. It handles loading states and data validation.
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isLoading - Loading state flag
 * @returns {Object} Object containing tree map data
 * @returns {TreeMapData[]} treeMapData - Array of tree map data points
 */
const useTreeMapData = ({ isLoading }: { isLoading: boolean }): { treeMapData: TreeMapData[] } => {
  const { t } = useTranslation();
  const { initiatives } = useFilteredInitiatives({ enableSearch: false });
  const kpiOverviewData = useStrategyStore((state) => state.kpiOverviewData);
  const treeMapMetric = useStrategyStore((state) => state.treeMapMetric);

  const treeMapData = useMemo(() => {
    if (!kpiOverviewData || initiatives.length === 0 || isLoading) {
      return [];
    }

    const dataset =
      treeMapMetric === TreeMapMetric.CostByInitiative
        ? (kpiOverviewData?.initiative_cost_breakdown ?? {})
        : (kpiOverviewData?.initiative_effort_breakdown ?? {});

    const hasValidDataset = Object.keys(dataset).length > 0;
    if (!hasValidDataset) {
      return [];
    }

    const data = calculateTreeMapData(dataset, initiatives, t);

    return data;
  }, [initiatives, treeMapMetric, kpiOverviewData, isLoading, t]);

  return { treeMapData };
};

export { useTreeMapData };
